import { create } from 'zustand';

/**
 * Options for a single confirm prompt. Title/description are plain strings —
 * resolve Lingui `` t`…` `` before calling `confirm()` (same contract as the
 * `toast` façade). `confirmLabel`/`cancelLabel` are optional; the host supplies
 * localized defaults when they are omitted. `variant` selects the confirm
 * button's `Button` variant.
 */
export interface ConfirmOptions {
  title: string;
  description?: string;
  /** Plain-string list of what the action affects, shown below the description. */
  items?: readonly string[];
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'default' | 'destructive';
}

/**
 * Single-instance confirm state. Lives in a Zustand store (per the "stores live
 * in a `stores/` segment" rule) rather than React context — matching
 * `sidebarStore`/`transferStore` and the toast surface. The imperative
 * {@link confirm} façade (`src/shared/lib/confirm.ts`) drives it and the single
 * `<ConfirmDialogHost/>` mounted at the root renders it.
 */
interface ConfirmDialogState {
  open: boolean;
  options: ConfirmOptions | null;
  resolve: ((value: boolean) => void) | null;
  /** Open a prompt. Any prior pending awaiter is resolved `false` before it is replaced. */
  request: (options: ConfirmOptions, resolve: (value: boolean) => void) => void;
  /** Resolve the pending promise and close. `options` are kept so the exit animation shows content. */
  respond: (result: boolean) => void;
}

export const useConfirmDialogStore = create<ConfirmDialogState>((set, get) => ({
  open: false,
  options: null,
  resolve: null,
  request: (options, resolve) => {
    get().resolve?.(false);
    set({ open: true, options, resolve });
  },
  respond: (result) => {
    get().resolve?.(result);
    set({ open: false, resolve: null });
  },
}));
